import {
    computed,
    defineComponent,
    onMounted,
    ref,
    watch,
} from "vue";
import { useRoute } from "vue-router";
import CopyIdButton from "../components/CopyIdButton.js";
import ImageFallback from "../components/imageFallback.js";
import { DefaultEntityClient } from "../core/api/EntityClient.js";
import type {
    LogRole,
    LogUser,
} from "../core/types.js";
import { sortProfileRoles } from "./users/profile/profileHelpers.js";
import {
    getMemberAvatar,
    getMemberDisplayName,
    getMemberTag,
} from "../utils.js";

export default defineComponent({
    setup() {
        const route = useRoute();
        const roles = ref<LogRole[]>([]);
        const members = ref<LogUser[]>([]);
        const loading = ref(false);
        const error = ref<string | null>(null);

        const roleId = computed(() => String(route.params["id"] ?? ""));
        const role = computed(() => roles.value.find((candidate) => candidate.id === roleId.value) ?? null);

        async function loadRole() {
            if (!roleId.value)
                return;

            loading.value = true;
            error.value = null;

            try {
                const loadedRoles = await DefaultEntityClient.getRoles();
                roles.value = sortProfileRoles(loadedRoles);

                const response = await DefaultEntityClient.searchMembers({
                    roleIds  : [ roleId.value ],
                    roleMode : "all",
                    limit    : 120,
                });

                members.value = response.members;
            } catch (cause) {
                error.value = cause instanceof Error ? cause.message : "Rôle introuvable.";
            } finally {
                loading.value = false;
            }
        }

        onMounted(loadRole);
        watch(roleId, loadRole);

        return () => (
            <div class="role-details-page">
                <div class="role-details-hero">
                    <div>
                        <span class="member-directory-eyebrow">Rôle serveur</span>
                        <h1>{role.value?.name ?? "Rôle"}</h1>
                        <p>{members.value.length} membre{members.value.length > 1 ? "s" : ""} affiché{members.value.length > 1 ? "s" : ""} avec ce rôle.</p>
                    </div>
                    <CopyIdButton id={roleId.value} label="Identifiant rôle" />
                </div>

                {error.value && <div class="member-directory-notice">{error.value}</div>}

                {!error.value && loading.value && (
                    <div class="member-directory-grid">
                        {[0, 1, 2].map((item) => <div key={item} class="member-directory-card member-directory-card-skeleton" />)}
                    </div>
                )}

                {!error.value && !loading.value && (
                    <div class="member-directory-grid">
                        {members.value.map((member) => (
                            <router-link key={member.id} to={`/users/${member.id}`} class="role-details-member-card">
                                <ImageFallback
                                    class="member-directory-avatar"
                                    src={getMemberAvatar(member as any)}
                                    default="https://cdn.discordapp.com/embed/avatars/0.png"
                                    alt={getMemberDisplayName(member as any)}
                                />
                                <div class="member-directory-card-main">
                                    <strong>{getMemberDisplayName(member as any)}</strong>
                                    <span>{getMemberTag(member as any)}</span>
                                </div>
                            </router-link>
                        ))}
                    </div>
                )}
            </div>
        );
    },
});
